import { useState } from "react";
import { useRecordContext } from "ra-core";
import { ArrowUpCircle, Loader2, CheckCircle2 } from "lucide-react";
import { usePromoteLead } from "./usePromoteLead";
import type { BldrOsContact } from "../types";

/**
 * Button to promote a lead to a customer.
 *
 * Only renders when contact_type === 'lead' (or undefined/null).
 * Shows a confirmation step to prevent accidental promotion.
 * Placed in ContactAside.
 */
export function PromoteLeadButton() {
  const record = useRecordContext<BldrOsContact>();
  const { promote, isPending } = usePromoteLead();
  const [confirming, setConfirming] = useState(false);

  if (!record) return null;

  // Already a customer — show badge instead
  const contactType = (record as Record<string, unknown>).contact_type as
    | string
    | undefined;

  if (contactType === "customer") {
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-md">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Customer
      </div>
    );
  }

  const name =
    `${record.first_name || ""} ${record.last_name || ""}`.trim() ||
    "This contact";

  if (confirming) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Promote <strong>{name}</strong> to customer?
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => {
              promote({ contactId: record.id as string, contactName: name });
              setConfirming(false);
            }}
            disabled={isPending}
            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3 h-3" />
            )}
            Confirm
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={isPending}
            className="px-2.5 py-1 rounded text-xs font-medium border text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer"
    >
      <ArrowUpCircle className="w-3.5 h-3.5" />
      Promote to Customer
    </button>
  );
}
